import React, { useEffect, useState } from 'react';
import {
  BoxV2 as Box,
  Flex,
  Table,
  TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import { FilterToggleButton, Loader } from '@/shared_elements';
import { CampaignListItem } from '@/apis/core/types';
import { FilterConfigs, FilterLoaders, TableHeaders } from './constants';
import { campaignController } from '@/apis/core';
import { convertTimeWithTimezone } from '@/utils/time';
import {
  getCnpListOptions,
  getRejectionCategoryOptions,
  getUseCaseOptions,
} from '@/apis/services';
import CampaignsByCspsDeclinedListingRow from '../../components/CampaignsByCspsDeclinedListingRow';
import { SelectItem } from '@/types';
import { MAX_EVENT_RECORDS } from '@/features/events/constants';

const CampaignsByCspsDeclined: React.FC = () => {
  const {
    configs: filterConfigs,
    appliedFilters,
    additionalFilters,
    candidateFilters,
    handleEdit: handleCandidateFiltersChange,
    handleApply: handleAppliedFiltersChange,
    setOptions: setFilterOptions,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });
  const [filtersShown, setFiltersShown] = useState(false);
  const [rejectionCategories, setRejectionCategories] = useState<SelectItem[]>(
    []
  );

  const fetchCampaigns = async () => {
    const response = await campaignController.getPrimaryDcaCampaigns(
      getQueries()
    );

    return {
      data: response?.records || [],
      rowsPerPage: response?.recordsPerPage || 10,
      total: response?.totalRecords || 0,
      page: response?.page || 1,
    };
  };

  const getQueries = () => {
    const { sharedOnDate, declineDate, ...restAppliedFilters } = appliedFilters;
    const [startDate, endDate] = sharedOnDate
      ? dateRangeStringToTuple(sharedOnDate)
      : [];
    const [startUpdateDate, endUpdateDate] = declineDate
      ? dateRangeStringToTuple(declineDate)
      : [];

    return {
      ...tableFilters,
      ...restAppliedFilters,
      ...additionalFilters,
      startDate,
      endDate,
      startUpdateDate,
      endUpdateDate,
      sharingStatus: 'DECLINED',
      channelId: '10DLC',
    };
  };

  const dateRangeStringToTuple = (dateRange?: unknown): unknown[] => {
    if (typeof dateRange === 'string' && dateRange.includes('-')) {
      const dates = (dateRange as string).replace(/\s/gi, '').split('-');
      if (dates.length === 2) {
        return [
          convertTimeWithTimezone(dates[0], 'YYYY-MM-DD'),
          convertTimeWithTimezone(dates[1], 'YYYY-MM-DD'),
        ];
      }
    }
    return [];
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalCampaigns,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: campaigns,
    pagination,
    filter: tableFilters,
  } = useTable<CampaignListItem>(fetchCampaigns, {
    cappingTotal: 10000,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'sharedOnDate',
      ascendingOrder: false,
    },
  });

  const getSanitizedUpdates = (filters: { provisional?: string }) => {
    const { provisional } = filters;
    const updates: { provisional?: string } = {};
    const validProvisional = ['true', 'false'];

    if (!validProvisional.includes(provisional || '')) {
      updates.provisional = 'false';
    }

    return updates;
  };

  useEffect(() => {
    const updates = getSanitizedUpdates(appliedFilters);

    if (Object.keys(updates).length > 0) {
      handleAppliedFiltersChange({ ...appliedFilters, ...updates });
    }
  }, [appliedFilters.provisional]);

  useEffect(() => {
    const updates = getSanitizedUpdates(candidateFilters);

    if (Object.keys(updates).length > 0) {
      handleCandidateFiltersChange({ ...candidateFilters, ...updates });
    }
  }, [candidateFilters]);

  const fetchFilterOptions = async () => {
    const [usecase, cnps, categories] = await Promise.allSettled([
      getUseCaseOptions(),
      getCnpListOptions(),
      getRejectionCategoryOptions(),
    ]);

    if (usecase.status === 'fulfilled') {
      setFilterOptions('usecase', usecase.value);
    }
    if (cnps.status === 'fulfilled') {
      setFilterOptions('downstreamCnpUid', cnps.value);
    }
    if (categories.status === 'fulfilled') {
      setFilterOptions('rejectionCategory', categories.value);
      setRejectionCategories(categories.value);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const handleFiltersToggle = () => {
    setFiltersShown(!filtersShown);
  };

  if (pageLoading) {
    return <Loader />;
  }

  return (
    <section data-testid="campaignsByCspsDeclined">
      <Flex
        sx={{
          alignItems: 'center',
          mb: 'xs',
          fontWeight: 600,
          fontSize: 'H600',
          lineHeight: '32px',
          color: 't.black80',
        }}
      >
        <span>
          {!pageLoading && totalCampaigns !== 0 ? `${totalCampaigns} ` : ' '}
          10DLC Declined Campaigns
        </span>
        <Box
          sx={{
            mt: '5px',
            ml: '12px',
            fontWeight: 400,
            fontSize: 'H200',
            lineHeight: '14px',
            color: 't.black60',
          }}
        >
          Results capped at first {MAX_EVENT_RECORDS.toLocaleString('en-US')}{' '}
          records
        </Box>
      </Flex>
      <Box sx={{ mt: 'xs', mb: 's' }}>
        <FilterToggleButton
          visible={filtersShown}
          onClick={handleFiltersToggle}
        />
      </Box>
      {filtersShown && (
        <Box sx={{ '.MuiAutocomplete-root': { width: 'auto' }, my: 's' }}>
          <TableFilter
            portal="dca"
            configs={filterConfigs}
            candidateValues={candidateFilters}
            appliedValues={appliedFilters}
            onCandidateValuesChange={handleCandidateFiltersChange}
            onAppliedValuesChange={handleAppliedFiltersChange}
            data-testid="campaignsByCspsDeclinedTableFilter"
          />
        </Box>
      )}
      <Table
        testId="campaignsByCspsDeclinedTable"
        headRows={TableHeaders}
        loading={tableLoading}
        emptyState="no campaigns to view"
        tableData={campaigns.map((record: CampaignListItem) => (
          <CampaignsByCspsDeclinedListingRow
            key={record.uid}
            campaign={record}
            categoryOptions={rejectionCategories}
          />
        ))}
        handleChangePage={handlePageChange}
        createSortHandler={handleSortingChange}
        filter={tableFilters}
        pagination={pagination}
      />
    </section>
  );
};

export default CampaignsByCspsDeclined;
